import { React, useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Card,
  Accordion,
} from '@mui/material';
import SportsIcon from '@mui/icons-material/Sports';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit'; // Иконка редактирования
import StyledTextField from './StyledTextField'; // Импортируем новый компонент

import {
  handleSubcript,
  getTrainingPlayers,
  fetchTrainingsData,
  unsubscribePlayer,
  patchTrainingsData,
} from '../api'; // Функция для PATCH запроса
import { useHookState } from '../NotificationApiHook';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { AuthContext } from '../../contexts/AuthContext';
import { getTime } from '../features/Bookings/utils';
import ConfirmUnsubscribeDialog from './ConfirmUnsubscribeDialog';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LoadingButton from '@mui/lab/LoadingButton';

const TrainingModal = ({ open, onClose, training, profile, placeId, selectedDay }) => {
  const { isLoggedIn, token } = useContext(AuthContext);
  const [players, setPlayers] = useState([]);
  const [playersQty, setPlayersQty] = useState(training?.players || 0);
  const [loading, setLoading] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const { notificationState } = useHookState();
  const [isAnotherUser, setIsAnotherUser] = useState(false);
  const [aliasName, setAliasName] = useState(profile?.name || '');
  const [formError, setFormError] = useState('');
  const [confirmUnsubscribeOpen, setConfirmUnsubscribeOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const isAdmin = localStorage.getItem('role_id') === '1';
  const isCoach = localStorage.getItem('role_id') === '3';
  const [isEditing, setIsEditing] = useState(false); // Состояние для редактирования тренировки
  const [updatedTraining, setUpdatedTraining] = useState({
    quantity: training?.quantity,
    start_time: training?.start_time,
    end_time: training?.end_time,
    info: training?.info,
  });
  const [applyToAll, setApplyToAll] = useState(false);
  const userId = localStorage.getItem('user_id');

  useEffect(() => {
    if (open && training) {
      fetchPlayers();
      setIsAnotherUser(false);
      setAliasName(profile?.name || '');
      setPlayersQty(training?.players);
      setFormError('');
      setUpdatedTraining({
        quantity: training?.quantity,
        start_time: getTime(training.start_time),
        end_time: getTime(training.end_time),
        info: training?.info,
      });
    }
  }, [open, training]);

  const formatDay = (isoDay) => {
    const parsedDate = parseISO(isoDay);
    return format(parsedDate, 'dd MMMM EEEE', { locale: ru });
  };

  if (!training) {
    return null;
  }

  const refreshTrainingDetails = async () => {
    try {
      const trainings = await fetchTrainingsData(placeId, selectedDay);
      const updatedTraining = trainings.find((t) => t.id === training.id);
      if (updatedTraining) {
        setPlayersQty(updatedTraining.players);
      } else {
        console.error('Training not found in the response');
      }
    } catch (error) {
      console.error('Error fetching trainings:', error);
    }
  };

  const subscript = async () => {
    setLoading(true);
    if (!aliasName) {
      setFormError('Имя обязательно');
      return;
    }

    const payload = {
      subscription_id: training.subscription_id,
      training_id: training.id,
      alias_name: aliasName,
      another_user: isAnotherUser ? true : false,
    };
    try {
      await handleSubcript(token, payload, notificationState);
      await fetchPlayers();
      await refreshTrainingDetails();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!selectedPlayer) return;
    try {
      const payload = {
        training_id: training.id,
        player_id: selectedPlayer.id,
        alias_name: selectedPlayer.alias_name,
      };
      await unsubscribePlayer(token, payload, notificationState);
      await fetchPlayers();
      await refreshTrainingDetails();
      setConfirmUnsubscribeOpen(false);
    } catch (error) {
      console.error('Error unsubscribing player:', error);
    }
  };

  const fetchPlayers = async () => {
    setLoadingPlayers(true);
    try {
      const playersList = await getTrainingPlayers(token, training.id, notificationState);
      setPlayers(playersList);
    } catch (error) {
    } finally {
      setLoadingPlayers(false);
    }
  };

  const handleApplyToAllChange = (e) => {
    setApplyToAll(e.target.checked);
  };

  const handleOpenUnsubscribeDialog = (player) => {
    setSelectedPlayer(player);
    setConfirmUnsubscribeOpen(true);
  };

  const handleCloseModal = () => {
    setPlayers([]);
    onClose();
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsAnotherUser(checked);
    if (checked) {
      setAliasName('');
    } else {
      setAliasName(profile?.name || '');
      setFormError(false);
    }
  };

  const handleEditTraining = () => {
    setIsEditing(true); // Включаем режим редактирования
  };

  const handleSaveChanges = async () => {
    try {
      const updatedData = applyToAll
        ? {
            place_id: placeId,
            name: training.info,
            quantity: updatedTraining.quantity,
            start_time_id: updatedTraining.start_time,
            end_time_id: updatedTraining.end_time,
            info: updatedTraining.info,
            for_all: true,
            day: training.day.replace(/"/g, ''),
          }
        : {
            id: training.id,
            quantity: updatedTraining.quantity,
            start_time_id: updatedTraining.start_time,
            end_time_id: updatedTraining.end_time,
            info: updatedTraining.info,
          };
      console.log(updatedData);
      await patchTrainingsData(token, notificationState, updatedData);
      setIsEditing(false);
      setApplyToAll(false);
      onClose(true);
    } catch (error) {
      console.error('Error updating training:', error);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiPaper-root': {
            maxHeight: 'calc(100% - 0px)',
          },
        }}
      >
        <Stack
          sx={{
            margin: '0',
            fontWeight: '500',
            fontSize: '1.25rem',
            lineHeight: '1.6',
            padding: '16px 24px',
            flex: '0 0 auto',
            background: 'linear-gradient(100deg, rgba(95,81,166,1) 81%, rgba(75,75,75,1) 19%);',
            color: 'white',
            maxWidth: '431px',
          }}
        >
          {training?.subscription_name}
          <Typography variant='h6'>{training?.info}</Typography>
          <Stack
            direction='column'
            sx={{
              justifyContent: 'space-between',
              paddingTop: '10px',
            }}
          >
            {/* Форма редактирования */}
            {isEditing && (
              <>
                <StyledTextField
                  label='Название тренировки'
                  value={updatedTraining.info}
                  onChange={(e) => setUpdatedTraining({ ...updatedTraining, info: e.target.value })}
                />
                <StyledTextField
                  label='Максимальное количество участников'
                  value={updatedTraining.quantity}
                  onChange={(e) =>
                    setUpdatedTraining({ ...updatedTraining, quantity: e.target.value })
                  }
                />
                <StyledTextField
                  label='Время начала'
                  type='time'
                  value={updatedTraining.start_time}
                  onChange={(e) =>
                    setUpdatedTraining({ ...updatedTraining, start_time: e.target.value })
                  }
                />
                <StyledTextField
                  label='Время окончания'
                  type='time'
                  value={updatedTraining.end_time}
                  onChange={(e) =>
                    setUpdatedTraining({ ...updatedTraining, end_time: e.target.value })
                  }
                />
                {isAdmin && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={applyToAll}
                        onChange={handleApplyToAllChange}
                        color='secondary'
                      />
                    }
                    label='Применить для всех тренировок с таким названием'
                  />
                )}

                <Button
                  color='success'
                  variant='contained'
                  onClick={handleSaveChanges}
                  sx={{ marginBottom: '7px', marginTop: '7px' }}
                >
                  Сохранить изменения
                </Button>
              </>
            )}
            <Stack
              direction='row'
              alignItems='center'
              gap={1}
              mb={2}
            >
              <SportsIcon sx={{ color: '#fff972' }} />
              <Typography>{training?.coach_name}</Typography>
              <MilitaryTechIcon sx={{ color: '#fff972' }} />
              <Typography>{training?.rank}</Typography>
            </Stack>
            <Stack
              direction='row'
              alignItems='center'
              gap={1}
              mb={2}
            >
              <EditCalendarIcon sx={{ color: '#fff972' }} />
              <Typography>{formatDay(training?.day.replace(/"/g, ''))} </Typography>
            </Stack>
            <Stack
              direction='row'
              alignItems='center'
              gap={1}
              mb={2}
            >
              <AccessTimeIcon sx={{ color: '#fff972' }} />
              <Typography>
                {getTime(training.start_time)} - {getTime(training.end_time)}
              </Typography>
            </Stack>
            <Stack
              direction='row'
              alignItems='center'
              gap={1}
              mb={2}
            >
              <PersonIcon sx={{ color: '#fff972' }} />
              <Typography>{`${playersQty} / ${training?.quantity}`} </Typography>
            </Stack>
            <Stack
              direction='row'
              alignItems='center'
              gap={1}
              mb={2}
            >
              <TextField
                id='outlined-basic'
                label='Имя'
                variant='outlined'
                color='secondary'
                value={aliasName}
                placeholder={isAnotherUser ? 'Введите имя' : ''}
                onChange={(e) => setAliasName(e.target.value)}
                InputLabelProps={{
                  style: { color: 'secondary' }, // цвет текста лейбла
                }}
                InputProps={{
                  style: { color: '#9d9d9d' }, // цвет текста в поле
                  notchedOutline: {
                    borderColor: 'white', // цвет рамки
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'white', // цвет рамки
                    },
                    '&:hover fieldset': {
                      borderColor: 'white', // цвет рамки при наведении
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'white', // цвет рамки при фокусе
                    },
                    '&.Mui-focused .MuiInputBase-input': {
                      color: 'white', // цвет текста внутри при фокусе
                    },
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAnotherUser}
                    onChange={handleCheckboxChange}
                    color='secondary'
                  />
                }
                label='Другой человек'
              />
            </Stack>
            {formError && <Typography color='secondary'>{formError}</Typography>}
            <Card sx={{ background: '#ffffff24', marginBottom: '10px' }}>
              <Typography
                sx={{ fontSize: 14, marginBottom: '6px', paddingLeft: '10px', paddingTop: '6px' }}
                color='white'
              >
                При записи будет списано посещение с вашего абонемента. <br />
                Возврат посещения возможен, при отмене участия более чем за 8 часов от времени
                начала тренировки. <br />
                Или при наличии бесплатных отмен в абонементе
              </Typography>
            </Card>
            {!loading ? (
              <Button
                color='success'
                variant='contained'
                disabled={!isLoggedIn}
                onClick={subscript}
              >
                Записаться
              </Button>
            ) : (
              <LoadingButton
                disabled
                color='success'
                variant='contained'
                sx={{ height: '40px' }}
                loading
              />
            )}
            {(isAdmin || isCoach) && !isEditing && (
              <Button
                variant='outlined'
                color='secondary'
                startIcon={<EditIcon />}
                onClick={handleEditTraining}
                sx={{ marginTop: '7px' }}
              >
                Редактировать тренировку
              </Button>
            )}
          </Stack>

          {loadingPlayers ? (
            <CircularProgress size={24} />
          ) : (
            <>
              {players?.length === 0 ? (
                <Box sx={{ width: '90%', margin: '0 auto', mt: 3 }}>
                  <Paper
                    elevation={3}
                    sx={{ p: 2 }}
                  >
                    <Typography
                      variant='body1'
                      sx={{ textAlign: 'center' }}
                    >
                      Пока никто не записался на тренировку
                    </Typography>
                    <Typography
                      variant='h6'
                      sx={{ textAlign: 'center' }}
                    >
                      Будь первым!
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Accordion
                  sx={{
                    marginTop: '5px',
                    '& .MuiPaper-root': {
                      borderRadius: '4px',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ArrowDropDownIcon />}
                    aria-controls='panel2-content'
                    id='panel2-header'
                  >
                    <Typography
                      variant='h6'
                      sx={{ textAlign: 'center', marginBottom: '8px' }}
                    >
                      Игроки
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ maxWidth: '400px', margin: '0 auto' }}>
                      <List>
                        {players?.map((player) => (
                          <ListItem
                            key={player.id}
                            secondaryAction={
                              player.user_id == userId && (
                                <IconButton
                                  edge='end'
                                  aria-label='unsubscribe'
                                  onClick={() => handleOpenUnsubscribeDialog(player)}
                                  sx={{
                                    color: 'red', // цвет иконки для отписки
                                    '&:hover': {
                                      color: 'darkred', // цвет иконки при наведении
                                    },
                                  }}
                                >
                                  <CloseIcon />
                                </IconButton>
                              )
                            }
                          >
                            <ListItemText
                              primary={player.name}
                              secondary={player?.username}
                              sx={{ fontWeight: 600 }} // цвет текста элементов списка
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          )}
        </Stack>
        <DialogActions>
          <Button onClick={handleCloseModal}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <ConfirmUnsubscribeDialog
        open={confirmUnsubscribeOpen}
        onClose={() => {
          setConfirmUnsubscribeOpen(false);
          setIsEditing(false);
        }}
        onConfirm={handleUnsubscribe}
        player={selectedPlayer}
        training={training}
      />
    </>
  );
};

export default TrainingModal;
